/**
 * RailwayCalculationOrchestrator - Railway Calculation Business Logic Orchestrator
 *
 * Follows the same architectural pattern as PavedCalculationOrchestrator.
 * Centralizes all Railway business logic and coordinates service calls.
 *
 * Architecture: Service Layer → Business Logic Layer → UI Layer
 * Performance: Eliminates complex logic from UI components for better separation of concerns
 */

import { logDebug, logError, LogTag } from '../utils/utils';
import { RailwayCalculationService, IRailwayCalculationResult } from './RailwayCalculationService';
import { IRailwayData } from '../api/IApiResponse';
import { EProjectType } from '../enum/project';
import { setOutputData } from '../Dynamic/store/project/resultsSlice';
import { AppDispatch } from '../../reduxService';

/**
 * Railway output data structure following the standard project output pattern
 */
export interface IRailwayOutputData {
    railwayCalculation?: IRailwayCalculationResult;
    calculationType?: string;
    timestamp?: string;
    isDefault?: boolean;
    error?: {
        message: string;
        timestamp: string;
    };
}

/**
 * Business Logic Orchestrator for Railway Calculations
 *
 * Extracted from Results.tsx to centralize:
 * - Form data transformation logic
 * - Railway calculation coordination
 * - Output data structure creation
 * - Error handling and fallback strategies
 */
export class RailwayCalculationOrchestrator {
    /**
     * Orchestrate complete Railway calculation from form data to output structure
     *
     * @param formData - IRailwayData from the form
     * @returns Promise<IRailwayOutputData> - Structured output data for Redux store
     */
    /**
     * Orchestrate complete Railway calculation from form data to output structure
     * @param formData - IRailwayData from the form
     * @param initialGuessUnstabilized - Optional initial guess for unstabilized Ballast thickness (meters)
     * @param initialGuessStabilized - Optional initial guess for stabilized Ballast thickness (meters)
     */
    async orchestrateCalculation(formData: IRailwayData, initialGuessUnstabilized?: number, initialGuessStabilized?: number): Promise<IRailwayOutputData> {
        try {
            logDebug('RailwayCalculationOrchestrator', 'Starting Railway calculation orchestration with form data:', formData, LogTag.RAILWAY_CALC);

            // Validate input data
            if (!this.validateRailwayData(formData)) {
                throw new Error('Invalid Railway data provided to orchestrator');
            }

            // Execute Railway calculations using the dedicated service
            // Forza initialGuess a 0.01 se non specificato
            const railwayCalculation = RailwayCalculationService.calculateRailwayParameters(
                formData,
                initialGuessUnstabilized !== undefined ? initialGuessUnstabilized : 0.01,
                initialGuessStabilized !== undefined ? initialGuessStabilized : 0.01
            );

            logDebug('RailwayCalculationOrchestrator', 'Railway calculations completed:', railwayCalculation, LogTag.RAILWAY_CALC);

            // Structure output data following the standard pattern
            const outputData: IRailwayOutputData = {
                railwayCalculation,
                // Add any additional fields that might be needed by the Results component
                calculationType: 'railway',
                timestamp: new Date().toISOString(),
            };

            logDebug('RailwayCalculationOrchestrator', 'Railway orchestration completed successfully:', outputData, LogTag.RAILWAY_CALC);

            return outputData;
        } catch (error) {
            logError('RailwayCalculationOrchestrator', 'Error during Railway calculation orchestration:', error, LogTag.ERROR_HANDLING);

            // Return error state that can be handled by the UI
            return {
                error: {
                    message: error instanceof Error ? error.message : 'Unknown error during Railway calculation',
                    timestamp: new Date().toISOString(),
                },
                calculationType: 'railway',
                timestamp: new Date().toISOString(),
            };
        }
    }

    /**
     * Validate Railway form data before processing
     *
     * @param formData - Railway form data to validate
     * @returns boolean indicating if data is valid
     */
    private validateRailwayData(formData: IRailwayData): boolean {
        try {
            // Basic validation - ensure we have the required structure
            if (!formData) {
                logError('RailwayCalculationOrchestrator', 'No form data provided', undefined, LogTag.VALIDATION);
                return false;
            }

            // 🔧 FIX: Railway now uses FLAT structure like other projects (Paved/Airport/Unpaved)
            // REASON: Backend accepts both FLAT and NESTED, frontend standardized to FLAT after transformProjectFromBackend()
            // REMOVED: Legacy .default wrapper - Railway data is now at formData.{fields} directly
            // CHANGED: Unified with other project types for consistency

            // Validate essential sections exist directly in formData
            const hasRequiredSections = formData.soilProperties || formData.poissonRatios || formData.trackGeometry;

            if (!hasRequiredSections) {
                logError('RailwayCalculationOrchestrator', 'Missing required data sections (soilProperties, poissonRatios, trackGeometry)', undefined, LogTag.VALIDATION);
                return false;
            }

            logDebug('RailwayCalculationOrchestrator', 'Railway data validation passed', undefined, LogTag.VALIDATION);
            return true;
        } catch (error) {
            logError('RailwayCalculationOrchestrator', 'Error during Railway data validation:', error, LogTag.ERROR_HANDLING);
            return false;
        }
    }

    /**
     * Create a facade method similar to PavedCalculationOrchestrator for consistency
     * This allows for easier integration with existing UI patterns
     *
     * @param projectData - Full project data containing Railway information
     * @returns Promise<IRailwayOutputData> - Calculated output data
     */
    /**
     * Process Railway project with optional initial guesses for Ballast thickness
     * @param projectData - Full project data containing Railway information
     * @param initialGuessUnstabilized - Optional initial guess for unstabilized Ballast thickness (meters)
     * @param initialGuessStabilized - Optional initial guess for stabilized Ballast thickness (meters)
     */
    async processRailwayProject(projectData: { projectInfo: { data: IRailwayData } }, initialGuessUnstabilized?: number, initialGuessStabilized?: number): Promise<IRailwayOutputData> {
        logDebug('RailwayCalculationOrchestrator', 'Processing Railway project:', projectData, LogTag.RAILWAY_CALC);
        return this.orchestrateCalculation(projectData.projectInfo.data, initialGuessUnstabilized, initialGuessStabilized);
    }

    /**
     * Get default Railway calculation results for fallback scenarios
     * Uses service default values to provide consistent fallback behavior
     *
     * @returns IRailwayOutputData with default calculation results
     */
    /**
     * Get default Railway calculation results for fallback scenarios
     * Uses service default values to provide consistent fallback behavior
     * @param initialGuessUnstabilized - Optional initial guess for unstabilized Ballast thickness (meters)
     * @param initialGuessStabilized - Optional initial guess for stabilized Ballast thickness (meters)
     */
    getDefaultRailwayResults(initialGuessUnstabilized?: number, initialGuessStabilized?: number): IRailwayOutputData {
        logDebug('RailwayCalculationOrchestrator', 'Generating default Railway results', undefined, LogTag.RAILWAY_CALC);

        // 🔧 FIX: Railway now uses FLAT structure (no .default wrapper) - unified with other projects
        // BEFORE: nested under .default - AFTER: FLAT structure at root level
        const defaultRailwayData: IRailwayData = {
            type: EProjectType.railways,
            soilProperties: {},
            poissonRatios: {},
            trackGeometry: {},
        } as any;

        const defaultCalculation = RailwayCalculationService.calculateRailwayParameters(defaultRailwayData, initialGuessUnstabilized, initialGuessStabilized);

        return {
            railwayCalculation: defaultCalculation,
            calculationType: 'railway',
            timestamp: new Date().toISOString(),
            isDefault: true,
        };
    }

    /**
     * Extract Railway-specific data from a generic project structure
     * Useful for handling different project data formats
     *
     * @param projectData - Generic project data
     * @returns IRailwayData or null if not a valid Railway project
     */
    extractRailwayData(projectData: Record<string, unknown>): IRailwayData | null {
        try {
            // Check if this is a Railway project
            if (projectData?.type !== EProjectType.railways) {
                return null;
            }

            // Return the Railway data
            return projectData as unknown as IRailwayData;
        } catch (error) {
            logError('RailwayCalculationOrchestrator', 'Error extracting Railway data:', error, LogTag.ERROR_HANDLING);
            return null;
        }
    }
}

/**
 * Static method for direct Redux integration
 * Follows the pattern established by PavedCalculationOrchestrator
 *
 * @param formData - Railway form data
 * @param dispatch - Redux dispatch function
 */
export const runRailwayCalculation = async (formData: IRailwayData, dispatch: AppDispatch): Promise<void> => {
    try {
        logDebug('RailwayCalculationOrchestrator', 'Running Railway calculation with Redux integration', undefined, LogTag.REDUX_STATE);

        const orchestrator = new RailwayCalculationOrchestrator();
        const outputData = await orchestrator.orchestrateCalculation(formData);

        // Dispatch results to Redux store following the standard pattern
        dispatch(setOutputData(outputData as any));

        logDebug('RailwayCalculationOrchestrator', 'Railway calculation results dispatched to Redux', undefined, LogTag.REDUX_STATE);
    } catch (error) {
        logError('RailwayCalculationOrchestrator', 'Error in Railway Redux integration:', error, LogTag.ERROR_HANDLING);

        // Dispatch error state
        const errorOutput: IRailwayOutputData = {
            error: {
                message: error instanceof Error ? error.message : 'Railway calculation failed',
                timestamp: new Date().toISOString(),
            },
            calculationType: 'railway',
            timestamp: new Date().toISOString(),
        };

        dispatch(setOutputData(errorOutput as any));
    }
};
